"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import MenuOverlay from "../menu-overlay/MenuOverlay"; // <-- import the reusable overlay

/** ---- Strapi media (video) ---- */
type StrapiFile = {
  id: number;
  url: string;
  name: string;
  alternativeText?: string | null;
  caption?: string | null;
  ext?: string | null;
  mime?: string | null;
  size?: number | null;
};

function absoluteCmsUrl(path?: string | null) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  const base = process.env.NEXT_PUBLIC_STRAPI_URL || "";
  return `${base}${path}`;
}

type HeroMainProps = {
  title?: string | null;
  description?: unknown;
  videoURL?: string | null;
  video?: StrapiFile | null;

  /** Optional href for Contact (Menu is now an overlay trigger) */
  contactHref?: string;
  className?: string;
};

export default function HeroMain({
  title = null,
  description = null,
  videoURL = null,
  video = null,
  contactHref = "/contact",
  className = "",
}: HeroMainProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const menuRef = useRef<HTMLButtonElement | null>(null);
  const contactRef = useRef<HTMLAnchorElement | null>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const reducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  /** Prefer media.url from Strapi if provided, else explicit videoURL */
  const resolvedVideoUrl = useMemo(() => {
    if (video?.url) return absoluteCmsUrl(video.url);
    if (videoURL) return videoURL;
    return null;
  }, [video, videoURL]);

  // GSAP entrance animation for side labels
  useEffect(() => {
    if (reducedMotion) return;
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    tl.fromTo(
      menuRef.current,
      { x: -16, autoAlpha: 0 },
      { x: 0, autoAlpha: 1, duration: 0.5 },
    ).fromTo(
      contactRef.current,
      { x: 16, autoAlpha: 0 },
      { x: 0, autoAlpha: 1, duration: 0.5 },
      "-=0.25",
    );
    return () => {
      tl.kill();
    };
  }, [reducedMotion]);

  // Intersection observer for lazy video loading
  useEffect(() => {
    if (!resolvedVideoUrl || !videoRef.current) return;

    const video = videoRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !videoLoaded) {
            setVideoLoaded(true);
            video.load(); // Load the video when it becomes visible
            observer.unobserve(video);
          }
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, [resolvedVideoUrl, videoLoaded]);

  // Handle reduced motion for video autoplay
  useEffect(() => {
    if (!resolvedVideoUrl || !videoLoaded) return;
    const v = videoRef.current;
    if (!v) return;

    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      if (m.matches) {
        v.pause();
        v.removeAttribute("autoplay");
        v.removeAttribute("loop");
      }
    };
    apply();
    try {
      m.addEventListener("change", apply);
    } catch {
      m.addListener(apply);
    }
    return () => {
      try {
        m.removeEventListener("change", apply);
      } catch {
        m.removeListener(apply);
      }
    };
  }, [resolvedVideoUrl, videoLoaded]);

  return (
    <>
      <section
        className={`max-w-7xl w-full mx-auto relative reveal ${className}`}
        aria-label={title || "Featured"}
      >
        {/* Left vertical trigger (button now) */}
        <button
          ref={menuRef}
          type="button"
          onClick={() => setMenuOpen(true)}
          className="hidden xl:block absolute cursor-pointer -left-20 inset-y-1/2 left-rotate-text font-normal opacity-0 hover:underline underline-offset-2"
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          aria-controls="preintro-menu"
          aria-label="Open menu"
        >
          Menu
        </button>

        {/* Video */}
        <div className="w-full aspect-video md:aspect-[16/9] min-h-[450px] relative">
          <video
            ref={videoRef}
            className="w-full h-full object-cover absolute inset-0"
            src={videoLoaded ? resolvedVideoUrl || undefined : undefined}
            autoPlay={videoLoaded}
            loop={videoLoaded}
            muted
            playsInline
            preload="metadata"
            poster={
              resolvedVideoUrl
                ? `${resolvedVideoUrl}?thumbnail=true`
                : undefined
            }
            aria-label={title || "Background video"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
            }}
          >
            {/* Caption track for accessibility - empty since this is a background video */}
            <track
              kind="captions"
              srcLang="en"
              label="No captions available"
            />
          </video>
          {/* Placeholder to maintain aspect ratio during loading */}
          {!videoLoaded && (
            <div
              className="absolute inset-0 bg-gray-200 animate-pulse"
              aria-hidden="true"
            />
          )}
        </div>

        {/* Right vertical link */}
        <Link
          href={contactHref}
          ref={contactRef}
          className="hidden xl:block absolute -right-20 inset-y-1/2 right-rotate-text font-normal opacity-0 hover:underline underline-offset-2"
          aria-label="Contact"
        >
          Contact
        </Link>
      </section>

      {/* Reusable overlay */}
      <MenuOverlay open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
